import {
  formatDuration as dateFnsFormatDuration,
  FormatDistanceToken,
  Locale,
} from "date-fns";
import { defaults, inRange } from "lodash-es";

import {
  formatDistanceToken,
  intervalToDurationMaxDays,
} from "src/utils/datetime";
import { logger } from "src/utils/logger";

export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
) => value.toLocaleString("en-US", options ?? {});

const FORMAT_DISTANCE_SHORT_LOCALE = {
  xSeconds: "s",
  xMinutes: "m",
  xHours: "h",
  xDays: "d",
};

const FORMAT_DISTANCE_LONG_LOCALE = {
  xSeconds: "sec",
  xMinutes: "min",
  xHours: "hours",
  xDays: "days",
  xWeeks: "weeks",
  xMonths: "months",
  xYears: "years",
};

const hasKey = <T extends object>(obj: T, key: PropertyKey): key is keyof T =>
  key in obj;

// Exported for testing
export const shortEnLocale: Pick<Locale, "formatDistance"> = {
  formatDistance: (token: FormatDistanceToken, count) => {
    if (hasKey(FORMAT_DISTANCE_SHORT_LOCALE, token)) {
      return `${count}${FORMAT_DISTANCE_SHORT_LOCALE[token]}`;
    }
    logger.error(`Formatting error. Unknown token: ${token}`);
    // Fall back to our distance token formatter which is still readable though it breaks this format.
    return formatDistanceToken(token, count);
  },
};

// Exported for testing
export const longEnLocale: Pick<Locale, "formatDistance"> = {
  formatDistance: (token: FormatDistanceToken, count) => {
    if (hasKey(FORMAT_DISTANCE_LONG_LOCALE, token)) {
      const formatted = `${count} ${FORMAT_DISTANCE_LONG_LOCALE[token]}`;
      return formatted.endsWith("s") && count === 1
        ? formatted.slice(0, -1)
        : formatted;
    }
    logger.error(`Formatting error. Unknown token: ${token}`);
    // Fall back to our distance token formatter which is still readable though it breaks this format.
    return formatDistanceToken(token, count);
  },
};

type FormatDurationOptions = {
  locale?: "short" | "long";
  onlyFirstUnit?: boolean;
};

export const formatDuration = (
  durationInMs: number,
  optionsArg?: FormatDurationOptions,
) => {
  if (durationInMs < 1000) {
    return `${durationInMs}ms`;
  }

  if (durationInMs < 60000) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
      style: "unit",
      unit: "second",
      unitDisplay: optionsArg?.locale === "long" ? "short" : "narrow",
    }).format(durationInMs / 1000);
  }

  const duration = intervalToDurationMaxDays({
    start: 0,
    end: durationInMs,
  });

  // Hide seconds for multi-day durations to reduce noise
  if (duration.days) {
    delete duration.seconds;
  }

  const options = defaults(optionsArg, {
    locale: "short",
    onlyFirstUnit: false,
  });

  const formatted = dateFnsFormatDuration(duration, {
    locale: options.locale === "short" ? shortEnLocale : longEnLocale,
  });

  if (options.onlyFirstUnit) {
    return formatted.match(/^(\d+\s*\w+)/)?.[0] ?? formatted;
  }

  return formatted;
};

const getFractionDigits = (n: number) => {
  if (n >= 1) return 1;

  if (inRange(n, 0.01, 0)) {
    return 3;
  } else if (inRange(n, 0.1, 0.01)) {
    return 2;
  } else {
    return 1;
  }
};

/**
 * For numbers bigger than 1, it will format with 1 fraction digit.
 * For numbers between 0 and 1:
 * 0.00001 -> 0
 * 0.00006 -> 0.001
 * 0.001 -> 0.001
 * 0.01 -> 0.01
 * 0.1 -> 0.1
 *
 * Take a look at the tests for more examples.
 */
export const formatNumberWithDynamicPrecision = (number: number) =>
  formatNumber(number, {
    maximumFractionDigits: getFractionDigits(number),
  });
